<template>
  <div class="w-full h-full">
  <div class="flex flex-wrap items-center justify-between my-4 lg:my-8 pr-2 sticky top-0 z-20 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-gray-200 px-1 pt-4 pb-3 relative">
      <!-- title -->
      <div class="flex-grow flex items-center mb-4 pr-20 md:pr-0">
        <svg-icon
          type="mdi"
          size="32"
          :path="icons.farmers"
          class="text-navy"
        />

        <span class="ml-2 text-navy font-bold leading-7 text-2xl">
          Farmers
        </span>
      </div>

  <div class="flex items-center space-x-3 mb-4 w-full md:w-auto">
        <!-- Filters group (button + inline clear icon) -->
        <div class="flex items-center">
          <button
            :class="['relative h-8 pl-3 pr-2 inline-flex items-center rounded-md border text-xs font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light transition-colors', activeFarmerFilterCount>0 ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50']"
            ref="farmerFiltersButton"
            type="button"
            @click="openFilters"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1 text-gray-600" viewBox="0 0 20 20" fill="currentColor"><path d="M3 4a1 1 0 011-1h12a1 1 0 01.8 1.6l-4.8 6.4V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-3.999L3.2 4.6A1 1 0 013 4z"/></svg>
            <span class="leading-none">Filters</span>
            <span v-if="activeFarmerFilterCount > 0" class="ml-2 inline-flex items-center justify-center min-w-[1.1rem] h-5 px-1 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">{{ activeFarmerFilterCount }}</span>
          </button>
          <button
            v-if="activeFarmerFilterCount > 0"
            @click.stop="clearFarmerFilters"
            type="button"
            aria-label="Clear all farmer filters"
            class="ml-1 h-6 w-6 inline-flex items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-400 text-xs font-bold"
          >
            ×
          </button>
        </div>
        <!-- Search toolbar variant -->
        <SearchComponent
          variant="toolbar"
          width="xl"
          :show-full-word-toggle="false"
          :form="form"
          :url="route('app.farmer.index')"
          class="w-full md:w-auto"
        />
      </div>
      <!-- Action icons (Add / Bulk Upload) right side -->
      <div class="flex items-center space-x-2 mb-4 ml-4 md:static absolute top-3 right-3">
        <div v-if="['admin','contractor','consultant','consultant_user'].includes(role)" class="flex items-center space-x-2">
          <inertia-link
            :href="route('app.farmer.create')"
            class="flex items-center justify-center w-8 h-8 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
          >
            <svg-icon type="mdi" size="20" :path="icons.plus" class="text-white" />
            <span class="sr-only">Add Farmer</span>
          </inertia-link>
          <inertia-link
            :href="route('app.farmer.create')"
            class="flex items-center justify-center w-8 h-8 rounded-full bg-navy hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light"
          >
            <svg-icon type="mdi" size="20" :path="icons.upload" class="text-white" />
            <span class="sr-only">Bulk Upload Farms</span>
          </inertia-link>
        </div>
      </div>
  </div>

    <!-- (Removed separate Filters Trigger section - now integrated in header) -->

    <!-- Drawer Component -->
    <FilterDrawer :show="showFilters"
                  title="Farmer Filters"
                  :active-count="activeFarmerFilterCount"
                  @close="closeFilters"
                  @apply="applyFarmerFilters"
                  @clear="clearFarmerDraft">
      <div class="space-y-4">
        <div v-if="role === 'admin'">
          <label class="block text-xs font-medium text-gray-700 mb-1" for="org_dd">Organisation</label>
          <select id="org_dd" v-model="farmerDraft.organisation" class="block w-full bg-gray-200 border-gray-400 rounded-lg sm:text-sm focus:outline-none focus:border-gray-400 focus:ring-0">
            <option value="">All</option>
            <option v-for="(org, idx) in organisations" :key="idx" :value="org.hashid">{{ org.name }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1" for="consultant_dd_f">Assigned Consultant</label>
          <select id="consultant_dd_f" v-model="farmerDraft.user_filter" class="block w-full bg-gray-200 border-gray-400 rounded-lg sm:text-sm focus:outline-none focus:border-gray-400 focus:ring-0">
            <option value="">All Consultants</option>
            <option v-for="(consultant, key) in consultants" :value="consultant.id" :key="key">{{ consultant.first_name + ' ' + consultant.last_name }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1" for="coop_dd">Co-op Programme</label>
          <select id="coop_dd" v-model="farmerDraft.sustainability_filter" class="block w-full bg-gray-200 border-gray-400 rounded-lg sm:text-sm focus:outline-none focus:border-gray-400 focus:ring-0">
            <option value="">All</option>
            <option value="1">Participating</option>
            <option value="0">Not Participating</option>
            <option value="null">Not Set</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1" for="derog_dd">Derogation Status</label>
          <select id="derog_dd" v-model="farmerDraft.derogation_filter" class="block w-full bg-gray-200 border-gray-400 rounded-lg sm:text-sm focus:outline-none focus:border-gray-400 focus:ring-0">
            <option value="">All</option>
            <option value="1">Derogation</option>
            <option value="0">Not Derogation</option>
            <option value="null">Not Set</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1" for="orgnic_dd">Organic Status</label>
          <select id="orgnic_dd" v-model="farmerDraft.organic_filter" class="block w-full bg-gray-200 border-gray-400 rounded-lg sm:text-sm focus:outline-none focus:border-gray-400 focus:ring-0">
            <option value="">All</option>
            <option value="1">Organic</option>
            <option value="0">Not Organic</option>
            <option value="null">Not Set</option>
          </select>
        </div>
      </div>
    </FilterDrawer>

    <flash-message />

    <!-- Mobile Card List -->
    <div v-if="farmers.length > 0" class="space-y-3 md:hidden mt-2 pb-4">
      <div v-for="(farmer, index) in farmers" :key="'farmer-card-'+index" class="bg-white rounded-lg border border-gray-200 p-3 shadow-sm flex flex-col gap-2">
        <div class="flex justify-between items-start gap-3">
          <div class="min-w-0 flex-1">
            <h3 class="text-sm font-semibold text-navy truncate" :title="farmer.name">{{ farmer.name }}</h3>
            <p class="text-[11px] text-gray-500 truncate" :title="farmer.address">{{ farmer.address }}</p>
          </div>
          <div class="shrink-0 flex items-center gap-2">
            <LocationMark :colour="farmer.farm_location_set ? '#1E660B' : '#FE2121'" class="w-4 h-4" />
            <index-menu-item :links="indexLinks(farmer)" />
          </div>
        </div>
        <div class="grid grid-cols-3 gap-3 text-[11px] leading-tight text-gray-700">
          <div>
            <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Farms</p>
            <div class="font-semibold">{{ farmer.farms }}</div>
          </div>
          <div>
            <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Active Tasks</p>
            <div class="font-semibold">{{ farmer.active_tasks_count }}</div>
          </div>
          <div v-if="role == 'admin'">
            <p class="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Advisor</p>
            <div class="truncate" :title="farmer.advisor || 'N/A'">{{ farmer.advisor || 'N/A' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table View (Desktop) -->
    <div v-if="farmers.length > 0" class="hidden md:block">
      <div class="mt-2 pb-14 w-full overflow-x-auto">
        <table
          class="table-auto min-w-[760px] xs:!mr-0 w-full divide-y divide-gray-400 border-b border-gray-400 border-collapse rounded-lg"
        >
          <thead>
            <tr class="bg-gray-200 font-bold text-sm text-gray-900">
              <td class="px-2 py-2.5 whitespace-nowrap">Farmer</td>
              <td class="px-2 py-2.5 whitespace-nowrap">Address</td>
              <td class="px-2 py-2.5"></td>
              <td class="px-2 py-2.5 whitespace-nowrap" v-if="role == 'admin'">Advisor</td>
              <td class="px-2 py-2.5 whitespace-nowrap">Farms</td>
              <td class="px-2 py-2.5 whitespace-nowrap">
                <inertia-link href="#" @click="changeSortBy('tasks')">Tasks↓</inertia-link>
              </td>
              <td class="px-2 py-2.5 w-10"></td>
            </tr>
          </thead>

          <tbody class="bg-white">
            <tr
              class="odd:bg-white even:bg-slate-100 text-gray-900 text-sm"
              v-for="(farmer, index) in farmers"
              :key="index"
            >
              <td class="px-2 py-2.5 cursor-pointer whitespace-nowrap truncate" :title="farmer.name">
                <inertia-link :href="route('app.farmer.view', farmer.id)" class="block">
                  {{ farmer.name }}
                </inertia-link>
              </td>
              <!-- Constrained address column so actions column is always visible without panel resize -->
              <td class="px-2 py-2.5 max-w-[220px] truncate" :title="farmer.address">
                {{ farmer.address }}
              </td>
              <td class="px-2 py-2.5 text-center">
                <LocationMark :colour="farmer.farm_location_set ? '#1E660B' : '#FE2121'" />
              </td>
              <td class="px-2 py-2.5 whitespace-nowrap truncate" v-if="role == 'admin'" :title="farmer.advisor || 'N/A'">
                {{ farmer.advisor || 'N/A' }}
              </td>
              <td class="px-2 py-2.5 text-center">
                <div class="w-7 h-7 font-bold text-[11px] mx-auto bg-gray-600 text-white rounded-full flex items-center justify-center">
                  {{ farmer.farms }}
                </div>
              </td>
              <td class="px-2 py-2.5 text-center whitespace-nowrap" :title="farmer.active_tasks_count + ' active tasks'">
                {{ farmer.active_tasks_count }}
              </td>
              <td class="w-12 px-2 py-2.5 text-right">
                <index-menu-item :links="indexLinks(farmer)" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-center">
        <pagination class="mt-8 mb-4" :links="links" />
      </div>
    </div>

    <div v-if="farmers.length === 0" class="bg-white mt-8 px-6 py-4">
      <strong>No farmers found.</strong>
    </div>
  </div>
</template>

<script>
import AppLayout from "@/Layouts/App";
import Pagination from "@/Components/Pagination";
import IndexMenuItem from "@/Components/IndexMenuItem";
import _ from "lodash";
import LocationMark from "@/Components/Icons/LocationMark.vue";
import { mdiHomeSiloOutline, mdiPlus, mdiTrayArrowUp } from "@mdi/js";
import SearchComponent from "@/Components/SearchComponent.vue";
import FilterDrawer from "@/Components/FilterDrawer.vue";
import { countActiveFilters } from '@/Composables/useActiveFilters';

export default {
  name: "farmerManger",
  layout: [AppLayout],
  components: {
    Pagination,
    IndexMenuItem,
    LocationMark,
    SearchComponent,
    FilterDrawer,
  },
  props: {
    role: String,
    search: String,
    organisation: String,
    results: Object,
    paginate: String,
    consultants: Array,
    organisations: Array,
    user_filter: String, // currently selected consultant filter
    sustainability_filter: String, // coop_program tri-state filter
    derogation_filter: String,
    organic_filter: String,
  },

  data() {
    return {
      form: {
        search: this.search,
        organisation: this.organisation,
      },
      farmers: this.results["original"]["data"],
      links: this.results["original"]["meta"]["pagination"],
      link: this.paginate,
      icons: {
        farmers: mdiHomeSiloOutline,
        plus: mdiPlus,
        upload: mdiTrayArrowUp,
      },
      showFilters: false,
      farmerDraft: {},
    };
  },

  mounted() {
    // Hide task pins from the tasks page when viewing farmers page
    this.$emitter.emit('hideTaskPins');
    
    // Ensure base farmer filter object exists
    if (!this.$root.farmer) {
      this.$root.farmer = {};
    }

    // Normalize existing sort/by behavior
    this.$root.farmer.by =
      this.$root.farmer.sort == "name" ? "" : this.$root.farmer.by;

    // Apply persisted values from props first (retain selection even with no results)
    const mapping = {
      user_filter: this.user_filter,
      sustainability_filter: this.sustainability_filter,
      derogation_filter: this.derogation_filter,
      organic_filter: this.organic_filter,
      organisation: this.organisation,
    };
    Object.keys(mapping).forEach(k => {
      if (mapping[k] !== undefined && mapping[k] !== null && this.$root.farmer[k] === undefined) {
        this.$root.farmer[k] = mapping[k];
      }
    });

    // Fallback to empty strings only if still unset
    ['user_filter','sustainability_filter','derogation_filter','organic_filter','organisation'].forEach(k => {
      if (typeof this.$root.farmer[k] === 'undefined' || this.$root.farmer[k] === null) {
        this.$root.farmer[k] = '';
      }
    });
    
    // Set the mounted flag to track initialization completion
    this._isMounted = true;
    
    // Show farmer pins layer when entering the farmer page
    this.$emitter.emit('showFarmerPins');
    
    // Emit initial farmer locations after component is fully mounted
    this.$nextTick(() => {
      // Check component state before emitting
      if (!this._isDestroying && this._isMounted) {
        this.emitFarmerLocations();
      }
    });
  },
  
  watch: {
    // Add watcher for farmers to emit locations when filtered farmers change
    farmers: {
      handler() {
        // Emit farmer locations when farmers change (due to filtering)
        if (this._isMounted && !this._isDestroying) {
          this.$nextTick(() => {
            // Double-check component state before emitting
            if (!this._isDestroying && this._isMounted) {
              this.emitFarmerLocations();
            }
          });
        }
      },
      immediate: false
    }
  },

  methods: {
    openFilters(){
      const src = this.$root.farmer || {};
      this.farmerDraft = JSON.parse(JSON.stringify({
        organisation: src.organisation || '',
        user_filter: src.user_filter || '',
        sustainability_filter: src.sustainability_filter || '',
        derogation_filter: src.derogation_filter || '',
        organic_filter: src.organic_filter || '',
      }));
      this.showFilters = true;
    },
    closeFilters(){
      this.showFilters = false;
      this.$nextTick(()=>{ this.$refs.farmerFiltersButton && this.$refs.farmerFiltersButton.focus(); });
    },
    applyFarmerFilters(){
      Object.assign(this.$root.farmer, this.farmerDraft);
      if (this.$root.farmer.page) delete this.$root.farmer.page;
      // Build payload
      const payload = {};
      Object.entries(this.$root.farmer).forEach(([k,v])=>{ if(v !== '' && v !== undefined && v !== null) payload[k]=v; });
      this.showFilters = false;
      this.$inertia.get(route('app.farmer.index'), payload);
    },
    clearFarmerDraft(){
      ['organisation','user_filter','sustainability_filter','derogation_filter','organic_filter'].forEach(k => { this.farmerDraft[k]=''; });
    },
    clearFarmerFilters() {
      const keys = ['user_filter','sustainability_filter','derogation_filter','organic_filter','organisation','search'];
      keys.forEach(k => { delete this.$root.farmer[k]; });
      this.$root.farmer.sort = 'name';
      this.$root.farmer.by = '';
      this.$inertia.get(route('app.farmer.index'));
    },
    selectFarm(index, farmer) {
      this.$root.search.farmer.selected = index;
      this.$root.search.farmer.search = farmer;

      let vm = this;

      axios.post(route("app.farmer.map.view", index)).then(function (response) {
        vm.$root.farmer = response.data;
      });
    },
    filtering() {
      // Always reset pagination when applying new filters
      if (this.$root.farmer.page) delete this.$root.farmer.page;
      // Build payload excluding empty-string values
      const payload = {};
      Object.entries(this.$root.farmer).forEach(([k,v]) => {
        if (v !== '' && v !== undefined && v !== null) {
          payload[k] = v;
        }
      });
      this.$inertia.get(route("app.farmer.index"), payload);
    },

    changeSortByFilter() {
      this.$root.farmer.sort = "tasks";
      this.filtering();
    },

    changeSortBy(sort) {
      this.$root.farmer.sort = sort;
      this.$root.farmer.by = this.$root.farmer.by == "desc" ? "asc" : "desc";
      this.filtering();
    },
    indexLinks(farmer) {
      return {
        View: route("app.farmer.view", {
          tab: "info",
          farmer: farmer.id,
        }),
        Edit: route("app.farmer.edit", farmer.id),
        "New Task": route("app.task.create", farmer.id),
      };
    },
    
    // Emit farmer locations to map - gets ALL farmers matching current filters (not just current page)
    async emitFarmerLocations() {
      try {
        // Build the same filter parameters that are used for the main farmer query
        const params = { ...this.$root.farmer };
        
        // Remove pagination-specific params since we want all results
        delete params.page;
        
        // Call the farmer map-locations endpoint
        const response = await axios.get(route("app.farmer.map-locations"), { params });
        
        // Check if component is still mounted and not being destroyed before emitting
        if (this._isDestroying || !this._isMounted) {
          return;
        }
        
        // The API returns farmers in response.data.farmers.data (nested structure)
        const farmersData = response.data?.farmers?.data || [];
        
        // Filter out farmers with invalid coordinates to prevent map zooming to Africa
        const validFarmersData = farmersData.filter(farmer => {
          if (!farmer.farm_location || !Array.isArray(farmer.farm_location) || farmer.farm_location.length < 2) {
            return false;
          }
          const [lng, lat] = farmer.farm_location;
          // Check for null, undefined, or [0,0] coordinates which would place pins in Africa
          return lng != null && lat != null && lng !== 0 && lat !== 0;
        });
        
        if (validFarmersData && validFarmersData.length > 0) {
          this.$emitter.emit('showFarmerLocations', {
            farmers: validFarmersData
          });
        } else {
          // Clear farmer locations if no farmers have valid coordinates
          this.$emitter.emit('showFarmerLocations', {
            farmers: []
          });
        }
      } catch (error) {
        console.error('Error loading farmer locations for map:', error);
        
        // Check if component is still mounted and not being destroyed before clearing
        if (this._isDestroying || !this._isMounted) {
          return;
        }
        
        // Clear farmer locations if API call fails rather than showing incomplete data
        this.$emitter.emit('showFarmerLocations', {
          farmers: []
        });
      }
    },
  },
  beforeDestroy() {
    // Hide farmer pins from the map when navigating away from the farmer index page
    this.$emitter.emit('hideFarmerPins');
  },
  computed: {
    activeFarmerFilterCount() {
      // Exclude 'search' so simple text search does not trigger filter badge/clear icon
      return countActiveFilters(this.$root.farmer, ['user_filter','sustainability_filter','derogation_filter','organic_filter','organisation']);
    }
  }
};
</script>
